import { parseISO } from 'date-fns';
import LangEnum from '../../../lang/LangEnum';
import { GetAddressDTO, ListCustomerAddressDTO } from '../../../types/address.types';
import { formatDate, formatDateFromApi } from '../../../utils/dates';
import { formatCNPJ, formatCompleteAddress } from '../../../utils/formatters';
import { BrandSplitType } from '../../brand-split-settings/brandSplitSettings.types';
import { AvailabilityEnum, CompactAvailability } from '../../products/ProductTypes';
import { TranslateType } from '../../translation/translationContext.types';
import { OrderType } from '../interfaces/orderType.enum';
import { BrandSplitSettings } from '../order.types';
import { GetOrderConditionDTO } from './dtos/orderCondition.dto';
import { OrderCondition } from './interfaces/orderCondition.interface';
import { FreightOrigin } from './orderCondition.types';

const checkIfListCustomer = (
  item: ListCustomerAddressDTO | GetAddressDTO,
): item is ListCustomerAddressDTO => (item as ListCustomerAddressDTO).address !== undefined;

export const getAddressOptionLabel = (item: ListCustomerAddressDTO | GetAddressDTO) => {
  if (!item) {
    return '';
  }

  if (!checkIfListCustomer(item)) {
    return item ? formatCompleteAddress(item) : '';
  }
  return item?.address ? formatCompleteAddress(item?.address) : '';
};

export const getOrderAvailability = (orderType: OrderType) =>
  orderType === OrderType.BOTH ? [OrderType.READY_FOR_DELIVERY, OrderType.PRE_ORDER] : orderType;

export const hasSplit = (splitBrandSettings: BrandSplitSettings[]): boolean =>
  splitBrandSettings.some((sbs) =>
    [
      BrandSplitType.DELIVERY_WINDOW,
      BrandSplitType.AVAILABILITY,
      BrandSplitType.CLASSIFICATION_TYPE,
    ].includes(sbs.type),
  );

export const sortConditions = (a: OrderCondition, b: OrderCondition) => {
  const conditionA = `${a.orderType}${a.deliveryWindow?.name}${a.classification?.name}`;
  const conditionB = `${b.orderType}${b.deliveryWindow?.name}${b.classification?.name}`;
  if (conditionA > conditionB) {
    return 1;
  }
  if (conditionA < conditionB) {
    return -1;
  }
  return 0;
};

export const formatOrderConditionCompanyName = (
  companyName: string | undefined,
  address: GetAddressDTO | undefined,
) => {
  const name = companyName ? `${companyName}` : '';

  if (!address) {
    return name;
  }

  const hasCity = address?.city;
  const hasState = address?.state;

  const city = hasCity ? ` (${address.city}` : '';
  const state = hasState ? `${address.state})` : '';

  const addressValue = `${name}${city}${hasCity && hasState ? ' - ' : ''}${
    !hasCity && hasState ? '(' : ''
  }${state}${hasCity && !hasState ? ')' : ''}`;
  return addressValue;
};

export const formatOrderConditionCommercialName = (commercialName: string, cpfcnpj?: string) =>
  `${commercialName} ${cpfcnpj ? `(${formatCNPJ(cpfcnpj)})` : ''}`;

export const formatOrderConditionDeliveryAddress = (address: GetAddressDTO | undefined) => {
  const streetDescription = address?.streetDescription ? `${address.streetDescription}, ` : '';
  const streetNumber = address?.streetNumber ? `n°${address.streetNumber}, ` : '';
  const neighborhood = address?.neighborhood ? `${address.neighborhood}, ` : '';
  const city = address?.city ? `${address.city} - ` : '';
  const state = address?.state ? `${address.state}, ` : '';

  const addressValue = `${streetDescription}${streetNumber}${neighborhood}${city}${state}`;
  return addressValue;
};

export const formatOrderConditionDiscount = (orderCondition: GetOrderConditionDTO) => {
  if (!orderCondition.commercialDiscount || orderCondition.commercialDiscount <= 0) {
    return '';
  }

  const businessPolicies =
    orderCondition.orderConditionBusinessPolicies?.flatMap((ocbp) => ocbp.businessPolicy?.name) ||
    [];

  const labels = [orderCondition.commercialPolicy?.name, ...businessPolicies].filter(
    (item) => !!item,
  );
  return `${orderCondition.commercialDiscount.toFixed(2)}% - ${labels.join(', ')}`;
};

export const getAvailabilityChipLabel = (
  orderCondition: OrderCondition,
  translate: TranslateType,
): string | undefined => {
  const { orderType, deliveryWindow } = orderCondition;
  switch (orderType) {
    case OrderType.READY_FOR_DELIVERY:
      return translate(LangEnum.GLOBAL_READY_FOR_DELIVERY);
    case OrderType.PRE_ORDER:
      return deliveryWindow?.name || translate(LangEnum.GLOBAL_PRE_ORDER);
    case OrderType.BOTH:
    default:
      return;
  }
};

const CONDITION_AVAILABILITY_BY_ORDER_TYPE: Record<
  OrderType,
  AvailabilityEnum | CompactAvailability
> = {
  [OrderType.PRE_ORDER]: CompactAvailability.PRE_ORDER,
  [OrderType.READY_FOR_DELIVERY]: CompactAvailability.READY_FOR_DELIVERY,
  [OrderType.BOTH]: AvailabilityEnum.EMPTY,
};

export const getConditionAvailability = (orderType: OrderType) =>
  CONDITION_AVAILABILITY_BY_ORDER_TYPE[orderType];

const getConditionClassificationLabel = (
  orderCondition: OrderCondition,
  translate: TranslateType,
): string | undefined => {
  if (orderCondition.classification) {
    return orderCondition.classification.name;
  }

  return orderCondition.orderType === OrderType.BOTH
    ? translate(LangEnum.ORDER_PAGE_CONDITION_TAB_LABEL)
    : undefined;
};

export const getConditionSplitLabel = (
  orderCondition: OrderCondition,
  translate: TranslateType,
): string =>
  [
    getAvailabilityChipLabel(orderCondition, translate),
    getConditionClassificationLabel(orderCondition, translate),
  ]
    .filter(Boolean)
    .join(' · ');

export interface OrderConditionSplitLabelParts {
  availabilityLabel?: string;
  classificationLabel?: string;
}

export const getConditionSplitLabelParts = (
  orderCondition: OrderCondition,
  translate: TranslateType,
): OrderConditionSplitLabelParts => ({
  availabilityLabel: getAvailabilityChipLabel(orderCondition, translate),
  classificationLabel: getConditionClassificationLabel(orderCondition, translate),
});

export const formatOrderBillingForecastDate = (date?: Date | null): string | undefined => {
  if (!date) {
    return;
  }

  const formattedDate = formatDate(parseISO(formatDateFromApi(date)!));
  return formattedDate;
};

interface GetFreightOriginParams {
  shippingCostValue?: number | null;
  quoteId?: string | null;
  freightOrigin?: FreightOrigin | null;
}

export const getFreightOrigin = ({
  shippingCostValue,
  quoteId,
  freightOrigin,
}: GetFreightOriginParams): FreightOrigin | undefined => {
  if (shippingCostValue === null || shippingCostValue === undefined) {
    return undefined;
  }

  if (freightOrigin) {
    return freightOrigin;
  }

  return quoteId ? FreightOrigin.AUTOMATIC : FreightOrigin.MANUAL;
};
